import { Injectable, signal } from '@angular/core';
import {
  AvatarType,
  ChatEvent,
  ChatEventType,
  ChatFilter,
  RenderedChatEvent,
  StepKey,
  ViewMode,
} from './client-chat-thread.model';
import { extractStoryChatPayload } from './story-chat-adapter';

// ---------------------------------------------------------------------------
// Typewriter configuration
// ---------------------------------------------------------------------------

const TYPEWRITER_MS_PER_CHAR = 18;
const TYPEWRITER_INITIAL_DELAY_MS = 150;
const TYPEWRITER_PUNCTUATION_PAUSE_MS = 60;

/** How long the Send button stays in its "pressed" state before the message commits. */
const COMPOSER_SEND_FLASH_MS = 400;
/** Pause after the message commits before the next queue item starts. */
const COMPOSER_POST_SEND_DELAY_MS = 300;

function isPunctuation(ch: string): boolean {
  return ch === '.' || ch === '!' || ch === '?';
}

// ---------------------------------------------------------------------------
// Internal session state
// ---------------------------------------------------------------------------

interface ThreadSessionState {
  /** Source-of-truth events (full content, used for filter/reconcile). */
  events: ChatEvent[];
  /** Events with typewriter rendering state, used for display. */
  renderedEvents: RenderedChatEvent[];
  /** IDs already added to renderedEvents (dedup guard). */
  seenIds: Set<string>;
  /** Messages waiting to be animated in order. */
  appendQueue: ChatEvent[];
  /** True while a message is being animated (prevents concurrent drains). */
  queueRunning: boolean;
  /** Step key of the most recently applied payload. */
  activeStepKey: StepKey;
  activeFilter: ChatFilter;
  viewMode: ViewMode;
  loading: boolean;
  error: string | null;
  /** Text currently being typewriter-animated into the composer textarea. */
  composerDraft: string;
  /** True while the composer is being animated (typewriter in progress). */
  composerTyping: boolean;
  /** True for one tick after the simulated send fires (drives button flash). */
  composerSending: boolean;
}

const DEFAULT_STATE: ThreadSessionState = {
  events: [],
  renderedEvents: [],
  seenIds: new Set(),
  appendQueue: [],
  queueRunning: false,
  activeStepKey: '',
  activeFilter: 'all',
  viewMode: 'full',
  loading: false,
  error: null,
  composerDraft: '',
  composerTyping: false,
  composerSending: false,
};

// ---------------------------------------------------------------------------
// Rich demo fallback dataset — covers all 5 event types
// ---------------------------------------------------------------------------

function iso(minutesAgo: number): string {
  return new Date(Date.now() - minutesAgo * 60 * 1000).toISOString();
}

const DEMO_THREAD: ChatEvent[] = [
  // --- Initial AI greeting ---
  {
    id: 'demo-1',
    eventType: 'assistant_message_ai',
    createdAt: iso(62),
    content:
      "Hi! I'm AIFA, your Digital Assistant. I'm here to help while your Financial Assistant gets up to speed. How can I assist you today?",
    speakerType: 'ai_agent',
    speakerId: 'aifa',
    speakerDisplayName: 'AIFA',
    speakerRoleLabel: 'Digital Assistant',
    avatarType: 'aifa_icon',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- Client message ---
  {
    id: 'demo-2',
    eventType: 'client_message',
    createdAt: iso(60),
    content: "Hi, I wanted to check in on my account and see how my goals are tracking.",
    speakerType: 'client',
    speakerId: 'client',
    speakerDisplayName: 'Client',
    speakerRoleLabel: undefined,
    avatarType: 'client_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- AI response ---
  {
    id: 'demo-3',
    eventType: 'assistant_message_ai',
    createdAt: iso(59),
    content:
      "Great question! Based on your current progress, you're tracking well on your savings goal. I can pull a quick summary — or if you'd prefer to speak with your Financial Assistant directly, just let me know.",
    speakerType: 'ai_agent',
    speakerId: 'aifa',
    speakerDisplayName: 'AIFA',
    speakerRoleLabel: 'Digital Assistant',
    avatarType: 'aifa_icon',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- Client requests human ---
  {
    id: 'demo-4',
    eventType: 'client_message',
    createdAt: iso(57),
    content: "Actually, can I talk to my Financial Assistant? I have a few specific questions.",
    speakerType: 'client',
    speakerId: 'client',
    speakerDisplayName: 'Client',
    avatarType: 'client_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- AI → Human handoff ---
  {
    id: 'demo-5',
    eventType: 'system_handoff_event',
    createdAt: iso(56),
    content: 'AIFA is bringing in a Financial Assistant.',
    speakerType: null,
    avatarType: null,
    handoffReason: 'client_requested_human',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- Internal note from FA before joining ---
  {
    id: 'demo-6',
    eventType: 'conversation_note',
    createdAt: iso(55),
    content:
      'Client is asking about 401(k) contribution rate and emergency fund setup. Review last quarter goals before joining. AIFA flagged potential SLA risk — waiting 18m.',
    speakerType: 'human_fa',
    speakerId: 'fa-jordan',
    speakerDisplayName: 'Jordan Lee',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    intercomNoteId: 'icn-001',
    visibility: 'internal',
    status: 'sent',
  },
  // --- Human FA joins ---
  {
    id: 'demo-7',
    eventType: 'system_handoff_event',
    createdAt: iso(54),
    content: 'You are now chatting with Jordan Lee (Financial Assistant).',
    speakerType: null,
    avatarType: null,
    handoffReason: 'human_joined',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- Human FA messages ---
  {
    id: 'demo-8',
    eventType: 'assistant_message_human',
    createdAt: iso(53),
    content:
      "Hi! This is Jordan. Thanks for your patience. I've reviewed your account — your savings goal is on track. What specific questions did you have?",
    speakerType: 'human_fa',
    speakerId: 'fa-jordan',
    speakerDisplayName: 'Jordan Lee',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-9',
    eventType: 'client_message',
    createdAt: iso(51),
    content: "Yes, can we look at adjusting my contribution rate for the 401(k)?",
    speakerType: 'client',
    speakerId: 'client',
    speakerDisplayName: 'Client',
    avatarType: 'client_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-10',
    eventType: 'assistant_message_human',
    createdAt: iso(50),
    content:
      "Of course. Your current rate is 6%. We can bump it up to 8% or 10% depending on your comfort level. I can show you the impact on your next paycheck if you'd like.",
    speakerType: 'human_fa',
    speakerId: 'fa-jordan',
    speakerDisplayName: 'Jordan Lee',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-11',
    eventType: 'client_message',
    createdAt: iso(48),
    content: "Let's try 8% for now. I don't want to stretch the budget too much.",
    speakerType: 'client',
    speakerId: 'client',
    speakerDisplayName: 'Client',
    avatarType: 'client_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-12',
    eventType: 'assistant_message_human',
    createdAt: iso(47),
    content:
      "Makes sense. I'll note that and we can revisit in a few months. Anything else you want to cover today?",
    speakerType: 'human_fa',
    speakerId: 'fa-jordan',
    speakerDisplayName: 'Jordan Lee',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-13',
    eventType: 'client_message',
    createdAt: iso(45),
    content: "Actually, yes. I've been thinking about opening a separate emergency fund. What do you recommend?",
    speakerType: 'client',
    speakerId: 'client',
    speakerDisplayName: 'Client',
    avatarType: 'client_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-14',
    eventType: 'assistant_message_human',
    createdAt: iso(44),
    content:
      "Good idea. A lot of folks aim for 3–6 months of expenses in a high-yield savings account. We can set a target based on your monthly spend and then automate a small transfer each pay period.",
    speakerType: 'human_fa',
    speakerId: 'fa-jordan',
    speakerDisplayName: 'Jordan Lee',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- FA shift change handoff ---
  {
    id: 'demo-15',
    eventType: 'system_handoff_event',
    createdAt: iso(30),
    content: 'Jordan Lee is now offline. AIFA can continue or connect you to the next available Financial Assistant.',
    speakerType: null,
    avatarType: null,
    handoffReason: 'fa_offline',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- Second FA joins ---
  {
    id: 'demo-16',
    eventType: 'system_handoff_event',
    createdAt: iso(29),
    content: 'You are now chatting with Alex Rivera (Financial Assistant).',
    speakerType: null,
    avatarType: null,
    handoffReason: 'human_joined',
    visibility: 'client_visible',
    status: 'sent',
  },
  // --- Second FA internal note ---
  {
    id: 'demo-17',
    eventType: 'conversation_note',
    createdAt: iso(28),
    content:
      'Picking up from Jordan. Client agreed to 8% 401(k) rate and is interested in emergency fund setup. Action items: confirm 401(k) change, send emergency fund target calculator.',
    speakerType: 'human_fa',
    speakerId: 'fa-alex',
    speakerDisplayName: 'Alex Rivera',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    intercomNoteId: 'icn-002',
    visibility: 'internal',
    status: 'sent',
  },
  // --- Second FA messages ---
  {
    id: 'demo-18',
    eventType: 'assistant_message_human',
    createdAt: iso(27),
    content:
      "Hi, this is Alex — I'm picking up from Jordan. I've reviewed the notes. I'll put together a quick recap and email it to you by end of day. We'll have the 401(k) change in place for the next cycle.",
    speakerType: 'human_fa',
    speakerId: 'fa-alex',
    speakerDisplayName: 'Alex Rivera',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-19',
    eventType: 'client_message',
    createdAt: iso(25),
    content: "Perfect, thanks. I'll wait for that and reach out if I have follow-up questions.",
    speakerType: 'client',
    speakerId: 'client',
    speakerDisplayName: 'Client',
    avatarType: 'client_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
  {
    id: 'demo-20',
    eventType: 'assistant_message_human',
    createdAt: iso(24),
    content: "Anytime. Talk soon.",
    speakerType: 'human_fa',
    speakerId: 'fa-alex',
    speakerDisplayName: 'Alex Rivera',
    speakerRoleLabel: 'Financial Assistant',
    avatarType: 'human_initials',
    visibility: 'client_visible',
    status: 'sent',
  },
];

// ---------------------------------------------------------------------------
// Payload adapter — normalizes Story Service payload to ChatEvent[]
// ---------------------------------------------------------------------------

type RawMessage = {
  id?: string;
  role?: string;
  speaker?: string;
  speakerType?: string;
  content?: string;
  text?: string;
  message?: string;
  createdAt?: string;
  timestamp?: string;
  speakerDisplayName?: string;
  speakerRoleLabel?: string;
};

function resolveEventType(raw: RawMessage): ChatEventType {
  const role = (raw.role ?? raw.speaker ?? raw.speakerType ?? '').toLowerCase();
  if (role === 'client' || role === 'user') return 'client_message';
  if (role === 'aifa' || role === 'ai' || role === 'ai_agent') return 'assistant_message_ai';
  if (role === 'note' || role === 'conversation_note') return 'conversation_note';
  if (role === 'handoff' || role === 'system_handoff_event' || role === 'system') return 'system_handoff_event';
  return 'assistant_message_human';
}

function resolveAvatarType(eventType: ChatEventType): AvatarType {
  if (eventType === 'client_message') return 'client_initials';
  if (eventType === 'assistant_message_ai') return 'aifa_icon';
  if (eventType === 'assistant_message_human') return 'human_initials';
  return null;
}

export function chatThreadAdapter(raw: unknown): ChatEvent[] {
  if (!Array.isArray(raw)) return [];
  const messages = raw as RawMessage[];
  return messages
    .map((m, idx): ChatEvent | null => {
      const content = (m.content ?? m.text ?? m.message ?? '').trim();
      if (!content) return null;
      const eventType = resolveEventType(m);
      return {
        id: m.id ?? `payload-${idx}`,
        eventType,
        createdAt: m.createdAt ?? m.timestamp ?? new Date().toISOString(),
        content,
        speakerType:
          eventType === 'client_message'
            ? 'client'
            : eventType === 'assistant_message_ai'
            ? 'ai_agent'
            : eventType === 'assistant_message_human'
            ? 'human_fa'
            : null,
        speakerDisplayName: m.speakerDisplayName,
        speakerRoleLabel: m.speakerRoleLabel,
        avatarType: resolveAvatarType(eventType),
        visibility: eventType === 'conversation_note' ? 'internal' : 'client_visible',
        status: 'sent',
      };
    })
    .filter((e): e is ChatEvent => e !== null);
}

// ---------------------------------------------------------------------------
// Filter logic
// ---------------------------------------------------------------------------

function applyFilter(events: RenderedChatEvent[], filter: ChatFilter, viewMode: ViewMode): RenderedChatEvent[] {
  let result = events;

  if (viewMode === 'conversation') {
    result = result.filter(
      (e) => e.eventType !== 'conversation_note' && e.eventType !== 'system_handoff_event'
    );
  }

  if (filter === 'all') return result;
  return result.filter((e) => {
    switch (filter) {
      case 'client':   return e.eventType === 'client_message';
      case 'fa':       return e.eventType === 'assistant_message_human';
      case 'aifa':     return e.eventType === 'assistant_message_ai';
      case 'notes':    return e.eventType === 'conversation_note';
      case 'handoffs': return e.eventType === 'system_handoff_event';
      default:         return true;
    }
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toRendered(event: ChatEvent, immediate: boolean): RenderedChatEvent {
  return {
    ...event,
    renderedContent: immediate ? event.content : '',
    typingComplete: immediate,
  };
}

/** Client messages get the in-bubble typewriter effect (they appear statically). */
function needsBubbleTypewriter(_event: ChatEvent): boolean {
  return false;
}

/** FA messages are typed into the composer before being "sent". */
function needsComposerTypewriter(event: ChatEvent): boolean {
  return event.eventType === 'assistant_message_human';
}

// ---------------------------------------------------------------------------
// State service
// ---------------------------------------------------------------------------

/**
 * Per-session signal store for the multi-speaker chat thread.
 * Scoped to My Clients layout (no providedIn).
 */
@Injectable()
export class ClientChatThreadStateService {
  private readonly _state = signal<Record<string, ThreadSessionState>>({});
  readonly state = this._state.asReadonly();
  private readonly _sessionTimers = new Map<
    string,
    Set<ReturnType<typeof setTimeout>>
  >();

  /** Scroll-to-bottom callback injected by the component so the service can trigger it. */
  scrollToBottomFn: (() => void) | null = null;

  /**
   * Called by the demo component to expose a way for the service to read/clear
   * the real composer draft (managed by ClientChatDemoStateService).
   * Not used for rendering — the service owns composerDraft in its own state.
   */
  composerClearFn: (() => void) | null = null;

  private getOrDefault(sessionId: string): ThreadSessionState {
    return this._state()[sessionId] ?? { ...DEFAULT_STATE, seenIds: new Set() };
  }

  private update(sessionId: string, patch: Partial<ThreadSessionState>): void {
    this._state.update((prev) => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] ?? { ...DEFAULT_STATE, seenIds: new Set() }), ...patch },
    }));
  }

  private schedule(
    sessionId: string,
    cb: () => void,
    delayMs: number
  ): ReturnType<typeof setTimeout> {
    const handle = setTimeout(() => {
      this.unregisterTimer(sessionId, handle);
      cb();
    }, delayMs);
    const timers = this._sessionTimers.get(sessionId) ?? new Set();
    timers.add(handle);
    this._sessionTimers.set(sessionId, timers);
    return handle;
  }

  private unregisterTimer(
    sessionId: string,
    handle: ReturnType<typeof setTimeout>
  ): void {
    const timers = this._sessionTimers.get(sessionId);
    if (!timers) return;
    timers.delete(handle);
    if (timers.size === 0) {
      this._sessionTimers.delete(sessionId);
    }
  }

  private clearTimers(sessionId: string): void {
    const timers = this._sessionTimers.get(sessionId);
    if (!timers) return;
    for (const handle of timers) {
      clearTimeout(handle);
    }
    this._sessionTimers.delete(sessionId);
  }

  /**
   * Cancel active animation/timers for a session and reset queue flags.
   * Returns true when there was active animated work that got interrupted.
   */
  private cancelInFlightAnimations(sessionId: string): boolean {
    const s = this.getOrDefault(sessionId);
    const hadAnimatedWork =
      s.queueRunning || s.composerTyping || s.composerSending || s.appendQueue.length > 0;
    this.clearTimers(sessionId);
    if (!hadAnimatedWork) return false;
    this.update(sessionId, {
      appendQueue: [],
      queueRunning: false,
      composerTyping: false,
      composerSending: false,
      composerDraft: '',
    });
    this.composerClearFn?.();
    return true;
  }

  getState(sessionId: string): ThreadSessionState {
    return this._state()[sessionId] ?? { ...DEFAULT_STATE, seenIds: new Set() };
  }

  /** Story / fallback step marker for the session (`demoId:step`, `fallback`, or ``). */
  getActiveStepKey(sessionId: string): StepKey {
    return this.getOrDefault(sessionId).activeStepKey;
  }

  /** Returns filtered rendered events for display. */
  renderedFilteredEvents(sessionId: string): RenderedChatEvent[] {
    const s = this.getState(sessionId);
    return applyFilter(s.renderedEvents, s.activeFilter, s.viewMode);
  }

  /** Returns the current composer draft being animated (for binding to the textarea). */
  composerDraft(sessionId: string): string {
    return this.getState(sessionId).composerDraft;
  }

  /** True while a client message is being typewriter-animated into the composer. */
  composerTyping(sessionId: string): boolean {
    return this.getState(sessionId).composerTyping;
  }

  /** True for the brief moment the Send button is being "pressed". */
  composerSending(sessionId: string): boolean {
    return this.getState(sessionId).composerSending;
  }

  /**
   * Legacy alias — kept for backward compatibility.
   * Delegates to applyStepPayload with a synthetic step key.
   */
  loadFromPayload(sessionId: string, raw: unknown): void {
    this.applyStepPayload(sessionId, raw, `legacy:${Date.now()}`);
  }

  loadFallback(sessionId: string): void {
    this.cancelInFlightAnimations(sessionId);
    const rendered = DEMO_THREAD.map((e) => toRendered(e, true));
    const seenIds = new Set(DEMO_THREAD.map((e) => e.id));
    this.update(sessionId, {
      events: DEMO_THREAD,
      renderedEvents: rendered,
      seenIds,
      appendQueue: [],
      queueRunning: false,
      activeStepKey: 'fallback',
      loading: false,
      error: null,
      composerDraft: '',
      composerTyping: false,
      composerSending: false,
    });
  }

  /**
   * Primary entry point for story-driven updates.
   *
   * - If stepKey differs from activeStepKey (jump/reset/new demo):
   *   rebuild rendered list from snapshot immediately, clear queue.
   * - If stepKey matches (same step re-applied, idempotent):
   *   skip processing.
   * - Otherwise (forward step):
   *   process delta appends via animation queue; reconcile from snapshot if delta empty.
   */
  applyStepPayload(sessionId: string, raw: unknown, stepKey: StepKey): void {
    const { snapshotMessages, deltaAppendMessages } = extractStoryChatPayload(raw);

    // Fallback to demo data when payload is empty
    if (snapshotMessages.length === 0 && deltaAppendMessages.length === 0) {
      this.loadFallback(sessionId);
      return;
    }

    const current = this.getOrDefault(sessionId);

    // Idempotent: same step applied twice → no-op
    if (stepKey === current.activeStepKey) return;

    // If the user moved steps while a typewriter/queue animation is active,
    // cancel stale work and render the newest snapshot immediately.
    const interruptedAnimation =
      current.activeStepKey !== '' && this.cancelInFlightAnimations(sessionId);
    if (interruptedAnimation && snapshotMessages.length > 0) {
      this.hydrateFromSnapshot(sessionId, snapshotMessages, stepKey);
      return;
    }

    const hasExistingState = current.activeStepKey !== '' && current.activeStepKey !== 'fallback';

    if (!hasExistingState) {
      // Initial load: render pre-delta history statically, then animate delta.
      if (deltaAppendMessages.length > 0) {
        const deltaIds = new Set(deltaAppendMessages.map((m) => m.id));
        const preStepMessages = snapshotMessages.filter((m) => !deltaIds.has(m.id));
        this.hydrateFromSnapshot(sessionId, preStepMessages, stepKey);
        this.enqueueAppends(sessionId, deltaAppendMessages, stepKey);
      } else {
        this.hydrateFromSnapshot(sessionId, snapshotMessages, stepKey);
      }
      return;
    }

    // Detect backward jump/reset: if any currently rendered event ID is absent
    // from the new snapshot, this is a jump back.
    // Hydrate only the pre-delta history statically, then animate the delta so
    // the target step's new messages play through the normal animation flow.
    if (snapshotMessages.length > 0) {
      const newSnapshotIds = new Set(snapshotMessages.map((m) => m.id));
      const isBackwardJump = current.renderedEvents.some((e) => !newSnapshotIds.has(e.id));
      if (isBackwardJump) {
        if (deltaAppendMessages.length > 0) {
          // Render everything except the delta messages statically, then animate delta.
          const deltaIds = new Set(deltaAppendMessages.map((m) => m.id));
          const preStepMessages = snapshotMessages.filter((m) => !deltaIds.has(m.id));
          this.hydrateFromSnapshot(sessionId, preStepMessages, stepKey);
          this.enqueueAppends(sessionId, deltaAppendMessages, stepKey);
        } else {
          // No delta — render the full snapshot statically (nothing to animate).
          this.hydrateFromSnapshot(sessionId, snapshotMessages, stepKey);
        }
        return;
      }
    }

    // Forward step: use delta if available, otherwise reconcile from snapshot
    if (deltaAppendMessages.length > 0) {
      // Update source-of-truth events from snapshot first
      this.update(sessionId, {
        events: snapshotMessages.length > 0 ? snapshotMessages : current.events,
        activeStepKey: stepKey,
      });
      this.enqueueAppends(sessionId, deltaAppendMessages, stepKey);
    } else {
      // No delta — reconcile from snapshot (add any missing IDs without animation)
      this.reconcileFromSnapshot(sessionId, snapshotMessages, stepKey);
    }
  }

  /** Rebuild rendered list from snapshot immediately (no animation). */
  private hydrateFromSnapshot(sessionId: string, messages: ChatEvent[], stepKey: StepKey): void {
    this.clearTimers(sessionId);
    const rendered = messages.map((e) => toRendered(e, true));
    const seenIds = new Set(messages.map((e) => e.id));
    this.update(sessionId, {
      events: messages,
      renderedEvents: rendered,
      seenIds,
      appendQueue: [],
      queueRunning: false,
      activeStepKey: stepKey,
      loading: false,
      error: null,
      composerDraft: '',
      composerTyping: false,
      composerSending: false,
    });
    this.composerClearFn?.();
    this.scrollToBottomFn?.();
  }

  /** Add any snapshot messages not yet in renderedEvents (no animation). */
  private reconcileFromSnapshot(sessionId: string, messages: ChatEvent[], stepKey: StepKey): void {
    const s = this.getOrDefault(sessionId);
    const newMessages = messages.filter((m) => !s.seenIds.has(m.id));
    if (newMessages.length === 0) {
      this.update(sessionId, { events: messages, activeStepKey: stepKey });
      return;
    }
    const addedRendered = newMessages.map((e) => toRendered(e, true));
    const newSeenIds = new Set([...s.seenIds, ...newMessages.map((e) => e.id)]);
    this.update(sessionId, {
      events: messages,
      renderedEvents: [...s.renderedEvents, ...addedRendered],
      seenIds: newSeenIds,
      activeStepKey: stepKey,
    });
    this.scrollToBottomFn?.();
  }

  /** Add new messages to the animation queue and start draining if idle. */
  enqueueAppends(sessionId: string, messages: ChatEvent[], stepKey: StepKey): void {
    const s = this.getOrDefault(sessionId);
    const newMessages = messages.filter((m) => !s.seenIds.has(m.id));
    if (newMessages.length === 0) return;

    const newSeenIds = new Set([...s.seenIds, ...newMessages.map((e) => e.id)]);
    this.update(sessionId, {
      appendQueue: [...s.appendQueue, ...newMessages],
      seenIds: newSeenIds,
    });

    if (!s.queueRunning) {
      this.drainQueue(sessionId, stepKey);
    }
  }

  /** Process the next message in the queue. */
  drainQueue(sessionId: string, stepKey: StepKey): void {
    const s = this.getOrDefault(sessionId);

    // Stale-response guard
    if (s.activeStepKey !== stepKey) return;

    if (s.appendQueue.length === 0) {
      this.update(sessionId, { queueRunning: false });
      return;
    }

    this.update(sessionId, { queueRunning: true });

    const [next, ...rest] = s.appendQueue;
    this.update(sessionId, { appendQueue: rest });

    if (needsComposerTypewriter(next)) {
      // Client message: type into composer → flash send → commit to thread
      this.update(sessionId, { composerDraft: '', composerTyping: true, composerSending: false });
      this.schedule(sessionId, () => {
        this.tickComposerTypewriter(sessionId, next, stepKey, 0);
      }, TYPEWRITER_INITIAL_DELAY_MS);
    } else if (needsBubbleTypewriter(next)) {
      // FA message: add placeholder bubble, then typewriter-fill it
      const placeholder = toRendered(next, false);
      this.update(sessionId, {
        renderedEvents: [...this.getOrDefault(sessionId).renderedEvents, placeholder],
      });
      this.scrollToBottomFn?.();

      this.schedule(sessionId, () => {
        this.tickBubbleTypewriter(sessionId, next.id, stepKey, next.content, 0);
      }, TYPEWRITER_INITIAL_DELAY_MS);
    } else {
      // All other event types: render immediately
      const rendered = toRendered(next, true);
      this.update(sessionId, {
        renderedEvents: [...this.getOrDefault(sessionId).renderedEvents, rendered],
      });
      this.scrollToBottomFn?.();
      this.drainQueue(sessionId, stepKey);
    }
  }

  /**
   * Typewriter-animates a client message into the composer textarea.
   * When complete, flashes the Send button, then commits the message to the thread.
   */
  private tickComposerTypewriter(
    sessionId: string,
    event: ChatEvent,
    stepKey: StepKey,
    charIndex: number
  ): void {
    const s = this.getOrDefault(sessionId);
    if (s.activeStepKey !== stepKey) return;

    const fullContent = event.content;
    const nextIndex = charIndex + 1;
    const partial = fullContent.slice(0, nextIndex);
    const done = nextIndex >= fullContent.length;

    this.update(sessionId, { composerDraft: partial });

    if (!done) {
      const lastChar = fullContent[charIndex];
      const delay = isPunctuation(lastChar)
        ? TYPEWRITER_PUNCTUATION_PAUSE_MS
        : TYPEWRITER_MS_PER_CHAR;
      this.schedule(sessionId, () => {
        this.tickComposerTypewriter(sessionId, event, stepKey, nextIndex);
      }, delay);
      return;
    }

    // Typing complete — flash the send button
    this.update(sessionId, { composerTyping: false, composerSending: true });

    this.schedule(sessionId, () => {
      const s2 = this.getOrDefault(sessionId);
      if (s2.activeStepKey !== stepKey) return;

      // Commit message to thread and clear composer
      const rendered = toRendered(event, true);
      this.update(sessionId, {
        composerDraft: '',
        composerSending: false,
        renderedEvents: [...this.getOrDefault(sessionId).renderedEvents, rendered],
      });
      this.composerClearFn?.();
      this.scrollToBottomFn?.();

      this.schedule(sessionId, () => {
        this.drainQueue(sessionId, stepKey);
      }, COMPOSER_POST_SEND_DELAY_MS);
    }, COMPOSER_SEND_FLASH_MS);
  }

  /** Advance in-bubble typewriter by one character. Calls itself recursively via setTimeout. */
  tickBubbleTypewriter(
    sessionId: string,
    msgId: string,
    stepKey: StepKey,
    fullContent: string,
    charIndex: number
  ): void {
    const s = this.getOrDefault(sessionId);

    // Stale-response guard
    if (s.activeStepKey !== stepKey) return;

    const nextIndex = charIndex + 1;
    const partial = fullContent.slice(0, nextIndex);
    const done = nextIndex >= fullContent.length;

    const updatedRendered = s.renderedEvents.map((e) =>
      e.id === msgId
        ? { ...e, renderedContent: partial, typingComplete: done }
        : e
    );
    this.update(sessionId, { renderedEvents: updatedRendered });

    if (done) {
      this.drainQueue(sessionId, stepKey);
      return;
    }

    const lastChar = fullContent[charIndex];
    const delay = isPunctuation(lastChar)
      ? TYPEWRITER_PUNCTUATION_PAUSE_MS
      : TYPEWRITER_MS_PER_CHAR;

    this.schedule(sessionId, () => {
      this.tickBubbleTypewriter(sessionId, msgId, stepKey, fullContent, nextIndex);
    }, delay);
  }

  /**
   * @deprecated Use tickBubbleTypewriter. Kept for any external callers.
   */
  tickTypewriter(
    sessionId: string,
    msgId: string,
    stepKey: StepKey,
    fullContent: string,
    charIndex: number
  ): void {
    this.tickBubbleTypewriter(sessionId, msgId, stepKey, fullContent, charIndex);
  }

  /**
   * Immediately appends a locally-generated event (e.g. invite note, join event)
   * to both the source list and the rendered list, bypassing the animation queue.
   * Deduplication guard still applies.
   */
  appendLocalEvent(sessionId: string, event: ChatEvent): void {
    const s = this.getState(sessionId);
    if (s.seenIds.has(event.id)) return;
    const rendered: RenderedChatEvent = {
      ...event,
      renderedContent: event.content,
      typingComplete: true,
    };
    const newSeenIds = new Set(s.seenIds);
    newSeenIds.add(event.id);
    this.update(sessionId, {
      events: [...s.events, event],
      renderedEvents: [...s.renderedEvents, rendered],
      seenIds: newSeenIds,
    });
  }

  setFilter(sessionId: string, filter: ChatFilter): void {
    this.update(sessionId, { activeFilter: filter });
  }

  setViewMode(sessionId: string, mode: ViewMode): void {
    this.update(sessionId, { viewMode: mode });
  }

  setLoading(sessionId: string, loading: boolean): void {
    this.update(sessionId, { loading });
  }

  setError(sessionId: string, error: string | null): void {
    this.update(sessionId, { error, loading: false });
  }
}
